import { useMemo, useRef, useState, type ReactNode } from 'react';

import { Check, Copy } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupTextarea,
} from '~/components/ui/input-group';

import { useCopyToClipboard } from '~/hooks/useCopyToClipboard';
import { useIsDarkMode } from '~/hooks/useIsDarkMode';

/** Duration the copy icon shows a checkmark confirmation before reverting. */
const COPY_FEEDBACK_MS = 2000;

function valueToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return Object.prototype.toString.call(value);
  }
}

interface CopyableTextFieldProps {
  value?: unknown;
  copyValue?: string;
  onCopySuccess?: () => void;
  onCopyError?: (error: Error) => void;
  minRows?: number;
  maxRows?: number;
  hideUnderline?: boolean;
  hideHelperText?: boolean;
  'data-testid'?: string;
  language?: string; // enables syntax highlighting when provided
  multiline?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: ReactNode;
}

export function CopyableTextField({
  copyValue,
  value,
  onCopySuccess,
  onCopyError,
  minRows = 15,
  maxRows,
  hideUnderline = false,
  hideHelperText = false,
  'data-testid': dataTestId,
  language,
  ...textFieldProps
}: CopyableTextFieldProps) {
  // Tailwind dark mode toggles the `dark` class on <html>; track it reactively
  // to pick the syntax-highlight theme.
  const isDark = useIsDarkMode();
  const syntaxStyle = isDark ? dracula : oneLight;

  const [copied, setCopied] = useState<boolean>(false);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { copy } = useCopyToClipboard({
    onSuccess: () => {
      setCopied(true);
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      resetTimeoutRef.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
      onCopySuccess?.();
    },
    onError: onCopyError,
  });

  const rows = useMemo(() => {
    const text = valueToString(value);
    const lineCount = text.split('\n').length;
    let calculatedRows = Math.max(lineCount, minRows);

    if (maxRows !== undefined) {
      calculatedRows = Math.min(calculatedRows, maxRows);
    }

    return calculatedRows;
  }, [value, minRows, maxRows]);

  const handleCopy = () => {
    const textToCopy = copyValue ?? valueToString(value);
    void copy(textToCopy);
  };

  const textContent = valueToString(value);
  const multiline = Boolean(textFieldProps.multiline);
  const label = typeof textFieldProps.label === 'string' ? textFieldProps.label : undefined;
  const placeholder = textFieldProps.placeholder;
  const helperText = hideHelperText ? undefined : textFieldProps.helperText;

  // Syntax highlighted mode when language prop is provided
  if (language) {
    return (
      // Outer container: positions button, contains layout
      <div
        className="relative min-w-0 overflow-hidden rounded border border-border"
        data-testid={dataTestId}
      >
        {/* Icon-only copy button overlaid in the corner, as in most code
            viewers, so it costs no row of its own. The code reserves right
            padding for it instead of top padding. */}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                onClick={handleCopy}
                variant="ghost"
                size="icon-sm"
                aria-label={copied ? 'Copied' : 'Copy code'}
                className="absolute top-1.5 right-1.5 z-[1] bg-background/80 backdrop-blur-sm"
              >
                {copied ? <Check /> : <Copy />}
              </Button>
            }
          />
          <TooltipContent>{copied ? 'Copied' : 'Copy code'}</TooltipContent>
        </Tooltip>
        <SyntaxHighlighter
          language={language}
          style={syntaxStyle}
          customStyle={{
            margin: 0,
            padding: '12px',
            paddingRight: '48px',
            fontSize: '0.875rem',
            lineHeight: 1.4,
            overflowX: 'auto',
          }}
        >
          {textContent}
        </SyntaxHighlighter>
      </div>
    );
  }

  // Default read-only field mode: InputGroup composes the field and copy
  // button instead of absolutely positioning the button over the input.
  const copyButton = (
    <InputGroupButton onClick={handleCopy} variant="ghost">
      {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
      {copied ? 'Copied' : 'Copy'}
    </InputGroupButton>
  );

  return (
    <div className="min-w-0">
      <InputGroup
        data-testid={dataTestId}
        className={hideUnderline ? 'border-transparent' : undefined}
      >
        {multiline ? (
          <InputGroupTextarea
            value={textContent}
            readOnly
            rows={rows}
            aria-label={label}
            placeholder={placeholder}
            className="font-mono text-sm"
          />
        ) : (
          <InputGroupInput
            value={textContent}
            readOnly
            aria-label={label}
            placeholder={placeholder}
            className="font-mono text-sm"
          />
        )}
        <InputGroupAddon align={multiline ? 'block-end' : 'inline-end'}>
          {copyButton}
        </InputGroupAddon>
      </InputGroup>
      {helperText && <p className="mt-1 text-sm text-muted-foreground">{helperText}</p>}
    </div>
  );
}
